import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { catchError, from, switchMap } from 'rxjs';
import { LOGIN_SCOPES } from './auth-config';

/**
 * Attaches a Microsoft access token to the protected management API calls
 * (`/api/*`), while deliberately skipping:
 *   - `/api/public/*` — the preview metadata endpoint, which is open
 *   - `/p/*`          — the served demo files, which are open
 *
 * If no one is signed in we send the request anyway and let the backend answer
 * 401, rather than blocking here.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const isManagementApi =
    req.url.startsWith('/api/') && !req.url.startsWith('/api/public/');
  if (!isManagementApi) return next(req);

  const msal = inject(MsalService);
  const account =
    msal.instance.getActiveAccount() ?? msal.instance.getAllAccounts()[0];
  if (!account) return next(req);

  return from(
    msal.instance.acquireTokenSilent({ scopes: LOGIN_SCOPES, account }),
  ).pipe(
    switchMap((result) =>
      next(
        req.clone({
          // Send the ID token (audience = this app's client id), which is what
          // the backend validates — not the Graph access token from `result`.
          setHeaders: { Authorization: `Bearer ${result.idToken}` },
        }),
      ),
    ),
    // Token couldn't be acquired silently — let the call proceed and 401.
    catchError(() => next(req)),
  );
};
